import React, { Component } from 'react';
import _PaymentGatewayConfigService from 'modules/PaymentGatewayConfigService';
import _notification from 'modules/notification';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import PageHeader from 'components/enterprise/PageHeader';
import DropdownList from 'components/controls/DropdownList';
import MyInput from 'components/controls/MyInput';
import SecretRefField from 'components/controls/SecretRefField';
import ButtonComponent from 'components/controls/Button';
import { routesAdmin } from 'routes/Routes';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';

// Phase R6.5 (Admin-Configurable Payment Gateway): lets an administrator switch/configure the active
// payment gateway (CCAvenue today, or the new DEMO/Mock provider) without a source-code change. Mirrors
// LanguageConfiguration_new.jsx's exact shape (Phase C.2's own template for this kind of Admin-editable,
// EnterpriseConfiguration-backed screen). Deliberately scoped to CCAvenue + DEMO this phase — no
// Easebuzz/Paynimo fields — but the field-group-per-provider layout below is structured so a future real
// provider only needs its own conditional section added, not a restructuring of this screen.
//
// Every credential field here is a SecretRefField: the administrator enters the NAME of an environment
// variable (e.g. "CCAVENUE_WORKING_KEY"), never the secret value itself — the real secret is resolved at
// payment time on the server via SecretProvider.Get. This screen never receives, displays, or stores an
// actual secret value.
const PROVIDER_OPTIONS_BASE = [
    { Id: 'CCAvenue', Name: 'CCAvenue (live gateway)' }
];
const PROVIDER_OPTION_DEMO = { Id: 'DEMO', Name: 'DEMO / Mock Payment (testing only)' };

class PaymentGatewayConfiguration_new extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            activeProvider: 'CCAvenue',
            isDemoAllowed: false,
            ccAvenueMerchantIdSecretRef: '',
            ccAvenueWorkingKeySecretRef: '',
            ccAvenueAccessCodeSecretRef: '',
            ccAvenueGatewayUrl: '',
            ccAvenueRedirectUrl: '',
            ccAvenueCancelUrl: '',
            ccAvenueFrontendReturnUrl: '',
            formError: ''
        };
    }

    componentDidMount() {
        this.loadConfig();
    }

    loadConfig = async () => {
        this.setState({ loading: true });
        try {
            const response = await _PaymentGatewayConfigService.getPaymentGatewayConfig();
            const data = response.data || {};
            this.setState({
                loading: false,
                activeProvider: data.ActiveProvider || 'CCAvenue',
                isDemoAllowed: !!data.IsDemoAllowed,
                ccAvenueMerchantIdSecretRef: data.CCAvenueMerchantIdSecretRef || '',
                ccAvenueWorkingKeySecretRef: data.CCAvenueWorkingKeySecretRef || '',
                ccAvenueAccessCodeSecretRef: data.CCAvenueAccessCodeSecretRef || '',
                ccAvenueGatewayUrl: data.CCAvenueGatewayUrl || '',
                ccAvenueRedirectUrl: data.CCAvenueRedirectUrl || '',
                ccAvenueCancelUrl: data.CCAvenueCancelUrl || '',
                ccAvenueFrontendReturnUrl: data.CCAvenueFrontendReturnUrl || ''
            });
        } catch (e) {
            this.setState({ loading: false });
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onFieldChange = (event) => {
        this.setState({ [event.target.name]: event.target.value, formError: '' });
    }

    handleSave = async () => {
        this.setState({ loading: true, formError: '' });
        try {
            const response = await _PaymentGatewayConfigService.updatePaymentGatewayConfig({
                ActiveProvider: this.state.activeProvider,
                CCAvenueMerchantIdSecretRef: this.state.ccAvenueMerchantIdSecretRef,
                CCAvenueWorkingKeySecretRef: this.state.ccAvenueWorkingKeySecretRef,
                CCAvenueAccessCodeSecretRef: this.state.ccAvenueAccessCodeSecretRef,
                CCAvenueGatewayUrl: this.state.ccAvenueGatewayUrl,
                CCAvenueRedirectUrl: this.state.ccAvenueRedirectUrl,
                CCAvenueCancelUrl: this.state.ccAvenueCancelUrl,
                CCAvenueFrontendReturnUrl: this.state.ccAvenueFrontendReturnUrl
            });
            if (response.status === 200) {
                _notification.showMessage('success', 'Payment Gateway Configuration saved.');
                this.loadConfig();
            } else {
                this.setState({ loading: false, formError: (response.data || GENERAL_ERROR_MESSAGE) });
            }
        } catch (e) {
            this.setState({ loading: false });
            const serverMessage = e && e.response && e.response.data;
            this.setState({ formError: typeof serverMessage === 'string' ? serverMessage : GENERAL_ERROR_MESSAGE });
            console.log('Error: ', e);
        }
    }

    render() {
        const { activeProvider, isDemoAllowed, formError, loading } = this.state;
        const providerOptions = isDemoAllowed ? [...PROVIDER_OPTIONS_BASE, PROVIDER_OPTION_DEMO] : PROVIDER_OPTIONS_BASE;

        if (loading && !this.state.ccAvenueGatewayUrl && activeProvider === 'CCAvenue' && !formError)
            return <LoadingSpinner />

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Payment Gateway Configuration"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Payment Gateway Configuration' }]}
                />
                <div className="analysisWrap">
                    <div className="max1200 m-auto">
                        <div className="ent-panel">
                            <h4 className="heading4">Active Payment Gateway</h4>
                            <div className="admin-inner-wrap p-3">
                                <div className="row">
                                    <div className="col-sm-6 mb-2">
                                        <DropdownList
                                            name="activeProvider"
                                            value={activeProvider}
                                            onChange={this.onFieldChange}
                                            placeholder="Select Payment Gateway Provider"
                                            heading="Payment Gateway Provider"
                                            isRequired={true}
                                            options={providerOptions}
                                        />
                                    </div>
                                </div>
                                {!isDemoAllowed && (
                                    <div className="ent-field-help mb-2">
                                        DEMO / Mock Payment is not offered on this environment. Set the
                                        APP_ENVIRONMENT environment variable to a non-Production value
                                        (e.g. "Development" or "Staging") on this server to make it
                                        selectable — it can never be activated on a Production environment.
                                    </div>
                                )}
                                {activeProvider === 'DEMO' && (
                                    <div className="ent-field-help mb-2">
                                        Candidates will be able to complete payment instantly with no real
                                        gateway call — every DEMO transaction is recorded with
                                        Method="DEMO" and a "DEMO-" prefixed reference so it can never be
                                        mistaken for a genuine transaction. Do not leave this active once
                                        testing is complete.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {activeProvider === 'CCAvenue' && (
                        <div className="max1200 m-auto mt-4">
                            <div className="ent-panel">
                                <h4 className="heading4">CCAvenue Settings</h4>
                                <div className="admin-inner-wrap p-3">
                                    <div className="row">
                                        <div className="col-sm-6 mb-2">
                                            <SecretRefField
                                                name="ccAvenueMerchantIdSecretRef"
                                                value={this.state.ccAvenueMerchantIdSecretRef}
                                                onChange={this.onFieldChange}
                                                heading="Merchant ID (environment variable name)"
                                                isRequired={true}
                                                helpText="The name of the environment variable your IT team configured to hold the real CCAvenue Merchant ID — never the value itself."
                                            />
                                        </div>
                                        <div className="col-sm-6 mb-2">
                                            <SecretRefField
                                                name="ccAvenueWorkingKeySecretRef"
                                                value={this.state.ccAvenueWorkingKeySecretRef}
                                                onChange={this.onFieldChange}
                                                heading="Working Key (environment variable name)"
                                                isRequired={true}
                                                helpText="The name of the environment variable holding the real CCAvenue Working Key."
                                            />
                                        </div>
                                        <div className="col-sm-6 mb-2">
                                            <SecretRefField
                                                name="ccAvenueAccessCodeSecretRef"
                                                value={this.state.ccAvenueAccessCodeSecretRef}
                                                onChange={this.onFieldChange}
                                                heading="Access Code (environment variable name)"
                                                isRequired={true}
                                                helpText="The name of the environment variable holding the real CCAvenue Access Code."
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-sm-12 mb-2">
                                            <MyInput
                                                type="text"
                                                name="ccAvenueGatewayUrl"
                                                value={this.state.ccAvenueGatewayUrl}
                                                onChange={this.onFieldChange}
                                                heading="CCAvenue Gateway URL"
                                                isRequired={true}
                                                className="w-100"
                                            />
                                        </div>
                                        <div className="col-sm-6 mb-2">
                                            <MyInput
                                                type="text"
                                                name="ccAvenueRedirectUrl"
                                                value={this.state.ccAvenueRedirectUrl}
                                                onChange={this.onFieldChange}
                                                heading="Success Callback (Redirect) URL"
                                                isRequired={true}
                                                className="w-100"
                                            />
                                        </div>
                                        <div className="col-sm-6 mb-2">
                                            <MyInput
                                                type="text"
                                                name="ccAvenueCancelUrl"
                                                value={this.state.ccAvenueCancelUrl}
                                                onChange={this.onFieldChange}
                                                heading="Cancel Callback URL"
                                                isRequired={true}
                                                className="w-100"
                                            />
                                        </div>
                                        <div className="col-sm-12 mb-2">
                                            <MyInput
                                                type="text"
                                                name="ccAvenueFrontendReturnUrl"
                                                value={this.state.ccAvenueFrontendReturnUrl}
                                                onChange={this.onFieldChange}
                                                heading="Candidate Browser Return URL (after payment)"
                                                isRequired={true}
                                                className="w-100"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="max1200 m-auto mt-3">
                        {formError && <div className="color-red mb-2">{formError}</div>}
                        <div className="pt-2 buttonAlignCenter">
                            <ButtonComponent type="button" value="Save" onClick={this.handleSave} className="button-global button-primary autoHeight small" disabled={loading} />
                        </div>
                    </div>
                </div>
            </div>
        )
    };
}

export default PaymentGatewayConfiguration_new;
